// ---------------------------------------------------------------------------
// eCommerce Orders — Unified order table for Shopify, WooCommerce, etc.
// ---------------------------------------------------------------------------
// Provider-agnostic order records synced from eCommerce platforms.
// Used for: revenue attribution, customer value, purchase history,
// deal-to-order linkage, automation triggers.
//
// GDPR erasure: DELETE WHERE tenant_id = X (standard cascade).
// PII: customerEmail stored only if needed for matching, not displayed.
// ---------------------------------------------------------------------------

import { pgTable, uuid, text, integer, boolean, timestamp, index, uniqueIndex, jsonb } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const ECOMMERCE_PROVIDERS = ["shopify", "woocommerce", "stripe", "custom"] as const;
export type EcommerceProvider = (typeof ECOMMERCE_PROVIDERS)[number];

export const ORDER_STATUSES = [
  "pending", "processing", "completed", "cancelled", "refunded", "failed",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ecommerceOrders = pgTable(
  "ecommerce_orders",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    provider: text("provider").notNull(), // EcommerceProvider
    externalOrderId: text("external_order_id").notNull(),
    externalCustomerId: text("external_customer_id"),

    // Resolved person (from identity resolution)
    personId: uuid("person_id"), // FK to people — null if unresolved

    // Order details
    orderNumber: text("order_number"),
    status: text("status").notNull().default("pending"),
    currency: text("currency").notNull().default("EUR"),
    totalAmountCents: integer("total_amount_cents").notNull().default(0),
    subtotalAmountCents: integer("subtotal_amount_cents"),
    taxAmountCents: integer("tax_amount_cents"),
    shippingAmountCents: integer("shipping_amount_cents"),
    discountAmountCents: integer("discount_amount_cents"),

    // Line items (JSONB for flexibility across providers)
    lineItems: jsonb("line_items").$type<Array<{
      name: string;
      quantity: number;
      priceAmountCents: number;
      sku?: string;
      productId?: string;
    }>>().default([]),

    // Customer info for matching (not displayed)
    customerEmail: text("customer_email"), // For identity resolution
    customerName: text("customer_name"),

    // Metadata
    providerMetadata: jsonb("provider_metadata").default({}),
    isTest: boolean("is_test").notNull().default(false),

    // Timestamps
    orderedAt: timestamp("ordered_at", tz).notNull(),
    fulfilledAt: timestamp("fulfilled_at", tz),
    cancelledAt: timestamp("cancelled_at", tz),
    refundedAt: timestamp("refunded_at", tz),
    syncedAt: timestamp("synced_at", tz).defaultNow().notNull(),
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_ecom_orders_provider_ext").on(table.tenantId, table.provider, table.externalOrderId),
    index("idx_ecom_orders_tenant").on(table.tenantId),
    index("idx_ecom_orders_person").on(table.tenantId, table.personId),
    index("idx_ecom_orders_status").on(table.tenantId, table.status),
    index("idx_ecom_orders_ordered").on(table.tenantId, table.orderedAt),
    index("idx_ecom_orders_customer_email").on(table.tenantId, table.customerEmail),
  ],
);
